import { useEffect, useState } from "react";

/**
 * Global Goldly reward toast — fires on a window event so any page can trigger it.
 * Triggered by: window.dispatchEvent(new CustomEvent("hf:goldly", { detail: { amount, action } }))
 * Or by helper: showGoldlyReward(5, "like")
 */
export function showGoldlyReward(amount, action = "engagement") {
  if (!amount || amount <= 0) return;
  window.dispatchEvent(new CustomEvent("hf:goldly", { detail: { amount, action } }));
}

const ACTION_LABELS = {
  like: "for liking ❤️",
  comment: "for commenting 💬",
  follow: "for following 👥",
  view_profile: "for viewing profile 👁",
  share: "for sharing 📤",
  view_story: "for viewing a story 📖",
  engagement: "for engaging",
};

export default function GoldlyReward() {
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    const handler = (ev) => {
      const { amount, action } = ev.detail || {};
      if (!amount) return;
      const id = Math.random().toString(36).slice(2);
      setQueue(q => [...q, { id, amount, action }]);
      // Auto-remove after 6s
      setTimeout(() => setQueue(q => q.filter(x => x.id !== id)), 6500);
    };
    window.addEventListener("hf:goldly", handler);
    return () => window.removeEventListener("hf:goldly", handler);
  }, []);

  if (queue.length === 0) return null;

  return (
    <div className="fixed inset-x-0 top-1/3 z-[100] pointer-events-none flex flex-col items-center gap-3" data-testid="goldly-reward-stack">
      {queue.map((item, idx) => (
        <GoldlyToast key={item.id} amount={item.amount} action={item.action} stackOffset={idx}/>
      ))}
    </div>
  );
}

function GoldlyToast({ amount, action, stackOffset }) {
  // Phases: 0 = appear (pop in center), 1 = fly to wallet (after 4.5s)
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setPhase(1), 4500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="goldly-toast"
      className="reward-toast pointer-events-auto"
      style={{
        animation: phase === 0
          ? "rewardPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards"
          : "rewardFly 1.4s cubic-bezier(0.55, 0, 0.45, 1) forwards",
        marginTop: stackOffset * 8,
      }}
    >
      <div className="relative">
        {/* Glow burst */}
        <div className="absolute inset-0 rounded-3xl blur-2xl opacity-60" style={{ background: "radial-gradient(circle, #FFD700 0%, transparent 65%)" }}/>
        <div className="relative rounded-3xl border border-[#FF7A00]/60 bg-gradient-to-br from-[#FF7A00]/25 to-[#FF7A00]/15 backdrop-blur-lg px-5 py-3.5 shadow-2xl flex items-center gap-3">
          <div className="goldly-coin">🪙</div>
          <div>
            <div className="font-outfit font-extrabold text-2xl text-[#FF7A00] leading-none drop-shadow-lg">+{amount}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF7A00]/80">Goldly</div>
            <div className="text-[10px] text-white/70 mt-0.5">{ACTION_LABELS[action] || ACTION_LABELS.engagement}</div>
          </div>
        </div>
      </div>

      <style>{`
        .reward-toast { will-change: transform, opacity; }
        .goldly-coin {
          font-size: 2.2rem;
          animation: coinSpin 1.4s linear infinite;
          filter: drop-shadow(0 0 10px rgba(255,215,0,0.8));
        }
        @keyframes coinSpin {
          0%   { transform: rotateY(0deg) scale(1); }
          50%  { transform: rotateY(180deg) scale(1.12); }
          100% { transform: rotateY(360deg) scale(1); }
        }
        @keyframes rewardPop {
          0%   { transform: scale(0.3) translateY(20px); opacity: 0; }
          60%  { transform: scale(1.12) translateY(0); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes rewardFly {
          /* Flies to the top-right where the wallet/coin pill sits in TopBar */
          0%   { transform: scale(1) translate(0, 0);                       opacity: 1; }
          35%  { transform: scale(0.85) translate(0, 0);                    opacity: 1; }
          100% { transform: scale(0.18) translate(40vw, -38vh);             opacity: 0; }
        }
      `}</style>
    </div>
  );
}
